import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { Modal } from "antd"
import { Button } from "components"
import { useEffect, useRef, useState } from "react"
import { useAuthStore } from "stores"
import { APP_ID } from "utils/constants"

// The zustand-persisted auth state lives under this single localStorage key.
const AUTH_STORAGE_KEY = `${APP_ID}.auth`

// Build an immutable fingerprint of the current tab's auth/session values
// from the persisted auth store (org, company, user and token).
const readSessionSignature = () => {
  if (typeof window === "undefined") {
    return ""
  }

  try {
    const raw = window?.localStorage?.getItem(AUTH_STORAGE_KEY)

    if (!raw) {
      return ""
    }

    const { state } = JSON.parse(raw)

    return [
      state?.user?.userOrganisationId ?? "",
      state?.user?.currentCompanyId ?? "",
      state?.user?.userId ?? "",
    ].join("|")
  } catch {
    return ""
  }
}

const MultipleAccountsAlert = () => {
  const { isAuthenticated, user, signOut } = useAuthStore((state) => ({
    isAuthenticated: state.isAuthenticated,
    user: state.user,
    signOut: state.signOut,
  }))
  const [showAlert, setShowAlert] = useState(false)
  // Persist the last known signature so we can compare future storage mutations.
  const accountSignature = useRef(readSessionSignature())

  useEffect(() => {
    if (!isAuthenticated) {
      setShowAlert(false)
      return
    }

    accountSignature.current = readSessionSignature()
  }, [isAuthenticated, user?.userOrganisationId, user?.userId])

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    const handleStorageChange = (event: StorageEvent) => {
      if (event.storageArea !== window.localStorage) {
        return
      }

      if (event.key && event.key !== AUTH_STORAGE_KEY) {
        return
      }

      // When another tab logs out (manually or via idle timeout) the persisted
      // auth token is cleared (or isAuthenticated is set to false). In that
      // case sign this tab out immediately instead of showing the
      // multiple-accounts alert.
      let stillAuthenticated = false

      try {
        const raw = window?.localStorage?.getItem(AUTH_STORAGE_KEY)

        if (raw) {
          const { state } = JSON.parse(raw)
          stillAuthenticated =
            Boolean(state?.authToken) && state?.isAuthenticated !== false
        }
      } catch {
        stillAuthenticated = false
      }

      if (!stillAuthenticated) {
        signOut(true)
        return
      }

      const latestSignature = readSessionSignature()

      if (latestSignature !== accountSignature.current) {
        setShowAlert(true)
      }
    }

    // GitHub-style enforcement: if another tab mutates the account signature,
    // force the current tab to reload before it continues work.
    window.addEventListener("storage", handleStorageChange)

    return () => {
      window.removeEventListener("storage", handleStorageChange)
    }
  }, [isAuthenticated, signOut])

  if (!isAuthenticated) {
    return null
  }

  return (
    <Modal
      open={showAlert}
      footer={null}
      closable={false}
      centered
      // styles={{
      //   mask: {
      //     backgroundColor: "rgba(225, 225, 225, 0.9)",
      //   },
      // }}
    >
      <div className="bg-white p-6 rounded-lg text-center font-circular">
        <ExclamationTriangleIcon className="w-14 h-14 text-danger500 mx-auto mb-4" />
        <h2 className="text-lg font-semibold mb-2 font-circular">
          Your active account has changed
        </h2>
        <p className="text-padeBlack">
          You’ve switched to a different sub-account in another tab. Reload this
          page to sync it with your current sub-account.
        </p>
        <div className=" mt-8">
          <Button
            color="primary"
            onClick={() => window.location.reload()}
            className="w-full max-w-[200px]"
          >
            Reload
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default MultipleAccountsAlert
